import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchTsrFoodItems,
  fetchTsrFoodCategories,
  fetchTsrFoodPackages,
  fetchTsrFoodPackageByIdAdmin,
  fetchTsrFoodQuantityRoundRules,
  saveTsrFoodPackage
} from '../../api/tsrFoodApi';
import TsrAdminDataTable from '../../components/TsrAdminDataTable';
import {
  DEFAULT_FOOD_IMAGE,
  PERSON_OPTIONS,
  SERVING_UNITS,
  resolveFoodImage,
  toBool,
  calcRawTotalQuantity,
  calcProductionTotalQuantity,
  calcLineTotal,
  formatQtyDisplay,
  formatAmount,
  resolveHeadcounts,
  mapPackageDetailToAdminState,
  DEFAULT_QUANTITY_ROUND_RULES
} from '../../utils/orderFoodUtils';

const emptyPackageForm = {
  packageName: '',
  persons: 10,
  kidPersons: 0,
  notes: ''
};

function formatPackageDate(value) {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleDateString('en-IN');
}

const EllipsisText = ({ text, className = '', style = {}, lines = 1 }) => {
  const value = text || '';
  const clampStyle = lines === 1
    ? {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        minWidth: 0
      }
    : {
        overflow: 'hidden',
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        minWidth: 0
      };

  return (
    <div className={className} style={{ ...clampStyle, ...style }} title={value}>
      {value}
    </div>
  );
};

const DESCRIPTION_SHOW_MORE_MIN_LENGTH = 28;

const ItemDescriptionLine = ({ text, onShowMore }) => {
  const value = String(text || '').trim();
  if (!value) {
    return null;
  }

  const showMoreLink = value.length > DESCRIPTION_SHOW_MORE_MIN_LENGTH;

  return (
    <div
      className="d-flex align-items-center text-muted"
      style={{ fontSize: '0.78rem', lineHeight: 1.2, minWidth: 0, width: '100%' }}
    >
      <span className="text-truncate" style={{ minWidth: 0, flex: '1 1 auto' }} title={value}>
        {value}
      </span>
      {showMoreLink ? (
        <button
          type="button"
          className="btn btn-link btn-sm p-0 ms-1 flex-shrink-0 text-decoration-none"
          style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}
          onClick={(event) => {
            event.stopPropagation();
            onShowMore(value);
          }}
        >
          Show more
        </button>
      ) : null}
    </div>
  );
};

const TsrAdminCreatePackagePage = () => {
  const builderRef = useRef(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const [searchText, setSearchText] = useState('');
  const [packageSearchText, setPackageSearchText] = useState('');
  const [packageForm, setPackageForm] = useState(emptyPackageForm);
  const [selectedItems, setSelectedItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [foodItems, setFoodItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editingPackageId, setEditingPackageId] = useState(null);
  const [packageImageUrl, setPackageImageUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [loadingPackage, setLoadingPackage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [descriptionModal, setDescriptionModal] = useState(null);
  const [roundRules, setRoundRules] = useState([]);

  const mapCatalogItems = useCallback((itemsData) => {
    const filteredItems = (itemsData || []).filter((item) => item.Rate > 0);
    return filteredItems.map((item) => ({
      id: item.FoodItemId,
      name: item.ItemName,
      rate: Number(item.Rate) || 0,
      category: item.CategoryName,
      subCategory: item.SubCategoryName || '',
      description: item.Description || '',
      image: resolveFoodImage(item),
      defaultQtyPerPerson: Number(item.DefaultQtyPerPerson) > 0 ? Number(item.DefaultQtyPerPerson) : 1,
      defaultQtyPerKid: item.DefaultQtyPerKid == null || item.DefaultQtyPerKid === ''
        ? 0
        : Number(item.DefaultQtyPerKid),
      servingUnit: item.ServingUnit || 'pcs',
      scaleByPersons: item.ScaleByPersons === undefined ? true : toBool(item.ScaleByPersons)
    }));
  }, []);

  const loadPackages = useCallback(async () => {
    try {
      setPackagesLoading(true);
      const response = await fetchTsrFoodPackages();
      setPackages(Array.isArray(response?.data) ? response.data : []);
    } catch (err) {
      console.error('Failed to fetch packages', err);
    } finally {
      setPackagesLoading(false);
    }
  }, []);

  const resetPackageForm = useCallback(() => {
    setEditingPackageId(null);
    setPackageImageUrl('');
    setPackageForm({ ...emptyPackageForm });
    setSelectedItems([]);
  }, []);

  const openEditPackage = useCallback(async (pkg) => {
    try {
      setLoadingPackage(true);
      setError('');
      const response = await fetchTsrFoodPackageByIdAdmin(pkg.FoodPackageId);
      const mapped = mapPackageDetailToAdminState(response.data, foodItems);
      if (!mapped) {
        window.alert('Package details not found.');
        return;
      }

      setEditingPackageId(mapped.editingPackageId);
      setPackageImageUrl(mapped.packageImageUrl);
      setPackageForm(mapped.packageForm);
      setSelectedItems(mapped.selectedItems);
      builderRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) {
      console.error('Failed to load package', err);
      window.alert('Failed to load package details. Please try again.');
    } finally {
      setLoadingPackage(false);
    }
  }, [foodItems]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const [categoriesData, itemsData] = await Promise.all([
          fetchTsrFoodCategories(),
          fetchTsrFoodItems()
        ]);
        setCategories(categoriesData.data || []);
        setFoodItems(mapCatalogItems(itemsData.data));

        try {
          const roundRulesResponse = await fetchTsrFoodQuantityRoundRules();
          const rules = roundRulesResponse?.data;
          setRoundRules(Array.isArray(rules) && rules.length ? rules : DEFAULT_QUANTITY_ROUND_RULES);
        } catch (roundRulesError) {
          console.warn('Quantity round rules unavailable, using defaults', roundRulesError);
          setRoundRules(DEFAULT_QUANTITY_ROUND_RULES);
        }
      } catch (err) {
        console.error('Failed to fetch food data', err);
        setError('Failed to load food items. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
    loadPackages();
  }, [loadPackages, mapCatalogItems]);

  const categoryOptions = useMemo(() => {
    if (categories.length === 0) {
      return ['All'];
    }
    return ['All', ...new Set(categories.map((c) => c.CategoryName))];
  }, [categories]);

  const filteredItems = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();
    return foodItems.filter((item) => {
      const matchesCategory = filterCategory === 'All' || item.category === filterCategory;
      if (!matchesCategory) {
        return false;
      }
      if (!normalizedSearch) {
        return true;
      }
      const haystack = [
        item.name,
        item.category,
        item.subCategory,
        item.description
      ].join(' ').toLowerCase();
      return haystack.includes(normalizedSearch);
    });
  }, [filterCategory, foodItems, searchText]);

  const filteredPackages = useMemo(() => {
    const normalizedSearch = packageSearchText.trim().toLowerCase();
    if (!normalizedSearch) {
      return packages;
    }
    return packages.filter((pkg) => String(pkg.PackageName || '').toLowerCase().includes(normalizedSearch));
  }, [packages, packageSearchText]);

  const packageColumns = useMemo(() => [
    {
      name: 'Package Name',
      sortable: true,
      selector: (row) => row.PackageName || '',
      cell: (row) => <span className="fw-semibold">{row.PackageName}</span>
    },
    {
      name: 'Persons',
      sortable: true,
      selector: (row) => Number(row.Persons) || 0,
      width: '100px'
    },
    {
      name: 'Items',
      sortable: true,
      selector: (row) => Number(row.ItemCount) || 0,
      width: '90px'
    },
    {
      name: 'Total',
      sortable: true,
      selector: (row) => Number(row.TotalAmount) || 0,
      cell: (row) => formatAmount(row.TotalAmount || 0)
    },
    {
      name: 'Created',
      sortable: true,
      selector: (row) => row.CreatedDate || '',
      cell: (row) => <small>{formatPackageDate(row.CreatedDate)}</small>
    },
    {
      name: 'Actions',
      cell: (row) => (
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => openEditPackage(row)}
          disabled={loadingPackage}
        >
          Edit
        </button>
      ),
      ignoreRowClick: true,
      width: '100px'
    }
  ], [loadingPackage, openEditPackage]);

  const headcounts = useMemo(
    () => resolveHeadcounts(packageForm.persons, packageForm.kidPersons),
    [packageForm.persons, packageForm.kidPersons]
  );

  const selectedTotal = useMemo(() => {
    return selectedItems.reduce(
      (sum, item) => sum + calcLineTotal(item, packageForm.persons, packageForm.kidPersons),
      0
    );
  }, [selectedItems, packageForm.persons, packageForm.kidPersons]);

  const addItem = (item) => {
    const adultStep = Number(item.defaultQtyPerPerson) > 0 ? Number(item.defaultQtyPerPerson) : 1;

    setSelectedItems((prev) => {
      const existing = prev.find((x) => x.id === item.id);
      if (existing) {
        return prev.map((x) => {
          if (x.id === item.id) {
            const nextAdult = (Number(x.qtyPerAdult) || 0) + adultStep;
            return {
              ...x,
              qtyPerAdult: nextAdult,
              qtyPerPerson: nextAdult
            };
          }
          return x;
        });
      }
      const qtyPerAdult = adultStep;
      const qtyPerKid = Number(item.defaultQtyPerKid) > 0 ? Number(item.defaultQtyPerKid) : 0;
      return [...prev, {
        ...item,
        qtyPerAdult,
        qtyPerKid,
        qtyPerPerson: qtyPerAdult,
        servingUnit: item.servingUnit || 'pcs',
        scaleByPersons: item.scaleByPersons !== false
      }];
    });
  };

  const updateQtyField = (itemId, field, delta) => {
    setSelectedItems((prev) => prev
      .map((item) => {
        if (item.id !== itemId) {
          return item;
        }
        const current = Number(item[field]) || 0;
        const nextQty = Math.max(0, current + delta);
        const updates = { [field]: nextQty };
        if (field === 'qtyPerAdult') {
          updates.qtyPerPerson = nextQty;
        }
        return { ...item, ...updates };
      })
      .filter((item) => (Number(item.qtyPerAdult) || 0) > 0 || (Number(item.qtyPerKid) || 0) > 0));
  };

  const updateSelectedItem = (itemId, changes) => {
    setSelectedItems((prev) => prev.map((item) => {
      if (item.id !== itemId) {
        return item;
      }
      return { ...item, ...changes };
    }));
  };

  const removeSelectedItem = (itemId) => {
    setSelectedItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const savePackage = async () => {
    const packageName = packageForm.packageName.trim();
    if (!packageName || selectedItems.length === 0) {
      window.alert('Enter package name and add at least one food item.');
      return;
    }

    const persons = Number(packageForm.persons);
    const kidPersons = Number(packageForm.kidPersons) || 0;
    const itemsPayload = selectedItems.map((item, index) => {
      const rawTotalQuantity = calcRawTotalQuantity(item, persons, kidPersons);
      const productionTotalQuantity = calcProductionTotalQuantity(item, persons, kidPersons, roundRules);
      const lineTotal = calcLineTotal(item, persons, kidPersons);
      const qtyPerAdult = Number(item.qtyPerAdult ?? item.qtyPerPerson) || 0;
      return {
        FoodItemId: item.id,
        Rate: item.rate,
        QtyPerAdult: qtyPerAdult,
        QtyPerKid: Number(item.qtyPerKid) || 0,
        QtyPerPerson: qtyPerAdult,
        ServingUnit: item.servingUnit || 'pcs',
        ScaleByPersons: item.scaleByPersons ? 1 : 0,
        RawTotalQuantity: rawTotalQuantity,
        TotalQuantity: productionTotalQuantity,
        LineTotal: lineTotal,
        DisplayOrder: index + 1
      };
    });

    try {
      setSaving(true);
      const imageUrl = packageImageUrl || selectedItems[0]?.image || DEFAULT_FOOD_IMAGE;
      const payload = {
        PackageName: packageName,
        PackageImageUrl: imageUrl,
        Persons: persons,
        KidPersons: kidPersons,
        BasePersons: persons,
        Notes: packageForm.notes,
        IsTemplate: 0,
        Items: itemsPayload,
        UserId: 1
      };

      if (editingPackageId) {
        payload.FoodPackageId = editingPackageId;
      }

      const response = await saveTsrFoodPackage(payload);

      if (!response?.success) {
        window.alert(response?.message || 'Failed to save package.');
        return;
      }

      await loadPackages();

      if (editingPackageId) {
        window.alert('Package updated successfully.');
      } else {
        resetPackageForm();
        window.alert('Package saved successfully.');
      }
    } catch (err) {
      console.error('Failed to save package', err);
      window.alert('Failed to save package. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Food Packages</h4>
          <p className="text-muted mb-0">
            {editingPackageId
              ? 'Edit an existing package or create a new one below.'
              : 'Configure a package above, then browse existing packages below.'}
          </p>
        </div>
        {editingPackageId ? (
          <button type="button" className="btn btn-outline-secondary" onClick={resetPackageForm}>
            New Package
          </button>
        ) : null}
      </div>

      <div ref={builderRef} className="row g-4 mb-4">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div
              className="card-body p-4 d-flex flex-column"
              style={{ maxHeight: 'calc(89vh - 7rem)', minHeight: 0 }}
            >
              <h5 className="mb-3">{editingPackageId ? 'Edit Package' : 'Create Package'}</h5>

              <div className="row g-3 mb-3 flex-shrink-0">
                <div className="col-md-6">
                  <label className="form-label">Package Name</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Full Thali"
                    value={packageForm.packageName}
                    onChange={(event) => setPackageForm((prev) => ({ ...prev, packageName: event.target.value }))}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">Persons</label>
                  <select
                    className="form-select"
                    value={packageForm.persons}
                    onChange={(event) => {
                      const persons = Number(event.target.value);
                      setPackageForm((prev) => ({
                        ...prev,
                        persons,
                        kidPersons: Math.min(prev.kidPersons, persons)
                      }));
                    }}
                  >
                    {PERSON_OPTIONS.map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label">Kids</label>
                  <input
                    type="number"
                    min="0"
                    max={packageForm.persons}
                    className="form-control"
                    value={packageForm.kidPersons}
                    onChange={(event) => {
                      const kidPersons = Math.min(
                        packageForm.persons,
                        Math.max(0, Number(event.target.value) || 0)
                      );
                      setPackageForm((prev) => ({ ...prev, kidPersons }));
                    }}
                  />
                  <small className="text-muted">Adults: {headcounts.adults}</small>
                </div>
                <div className="col-md-3">
                  <label className="form-label">Category</label>
                  <select
                    className="form-select"
                    value={filterCategory}
                    onChange={(event) => setFilterCategory(event.target.value)}
                  >
                    {categoryOptions.map((category) => (
                      <option key={category} value={category}>{category}</option>
                    ))}
                  </select>
                </div>
                <div className="col-12">
                  <label className="form-label">Notes</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Optional"
                    value={packageForm.notes}
                    onChange={(event) => setPackageForm((prev) => ({ ...prev, notes: event.target.value }))}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">Search Food Items</label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <span className="fas fa-search" />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by item name, category, or description"
                      value={searchText}
                      onChange={(event) => setSearchText(event.target.value)}
                    />
                    {searchText ? (
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setSearchText('')}
                        title="Clear search"
                      >
                        ×
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <div
                className="order-food-items-scroll flex-grow-1 pe-1"
                style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
              >
                {loading ? (
                  <div className="text-center py-5">
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span className="ms-2">Loading food items...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-5 text-danger">
                    <span className="fas fa-exclamation-triangle me-2" /> {error}
                  </div>
                ) : filteredItems.length === 0 ? (
                  <div className="text-muted">
                    {searchText.trim()
                      ? 'No food items match your search.'
                      : 'No food items found for this category.'}
                  </div>
                ) : (
                  <div className="row g-3">
                    {filteredItems.map((item) => (
                      <div className="col-md-6" key={item.id}>
                        <div className="border rounded-3 p-2 d-flex gap-2 align-items-center" style={{ minWidth: 0 }}>
                          <img
                            src={item.image}
                            alt={item.name}
                            width="56"
                            height="56"
                            style={{ objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                            onError={(event) => {
                              if (event.currentTarget.src.indexOf('tsr-default') === -1) {
                                event.currentTarget.src = DEFAULT_FOOD_IMAGE;
                              }
                            }}
                          />
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <EllipsisText
                              text={item.name}
                              className="fw-semibold"
                              style={{ fontSize: '0.92rem' }}
                            />
                            <EllipsisText
                              text={item.category}
                              className="text-muted"
                              style={{ fontSize: '0.82rem' }}
                            />
                            <div className="text-primary" style={{ fontSize: '0.85rem' }}>{formatAmount(item.rate)}</div>
                            <ItemDescriptionLine
                              text={item.description}
                              onShowMore={(description) => setDescriptionModal({ name: item.name, description })}
                            />
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary flex-shrink-0"
                            onClick={() => addItem(item)}
                            type="button"
                            title={`Add ${item.name}`}
                          >
                            Add
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="card border-0 shadow-sm rounded-4 h-100">
            <div
              className="card-body p-4 d-flex flex-column"
              style={{ maxHeight: 'calc(100vh - 7rem)', minHeight: 0 }}
            >
              <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
                <h5 className="mb-0">Selected Items</h5>
                <div className="fw-semibold text-primary">{formatAmount(selectedTotal)}</div>
              </div>

              <div
                className="flex-grow-1 pe-1"
                style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}
              >
                {selectedItems.length === 0 ? (
                  <div className="text-muted">No items selected yet. Add items from the left panel.</div>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    {selectedItems.map((item) => {
                      const rawQty = calcRawTotalQuantity(
                        item,
                        packageForm.persons,
                        packageForm.kidPersons
                      );
                      const productionQty = calcProductionTotalQuantity(
                        item,
                        packageForm.persons,
                        packageForm.kidPersons,
                        roundRules
                      );
                      const lineTotal = calcLineTotal(
                        item,
                        packageForm.persons,
                        packageForm.kidPersons
                      );
                      const unit = item.servingUnit || 'pcs';

                      return (
                        <div
                          key={item.id}
                          className="border rounded-3 p-2 bg-light"
                          style={{ minWidth: 0 }}
                        >
                          <div className="d-flex align-items-start gap-2">
                            <img
                              src={item.image}
                              alt={item.name}
                              width="48"
                              height="48"
                              style={{ objectFit: 'cover', borderRadius: 8, flexShrink: 0 }}
                              onError={(event) => {
                                if (event.currentTarget.src.indexOf('tsr-default') === -1) {
                                  event.currentTarget.src = DEFAULT_FOOD_IMAGE;
                                }
                              }}
                            />
                            <div className="flex-grow-1" style={{ minWidth: 0 }}>
                              <EllipsisText
                                text={item.name}
                                className="fw-semibold"
                                style={{ fontSize: '0.9rem' }}
                              />
                              <small className="text-muted d-block">{formatAmount(item.rate)} per {unit}</small>
                              <small className="text-primary d-block">
                                {item.scaleByPersons
                                  ? `Calculated: ${formatQtyDisplay(rawQty)} ${unit} → Kitchen: ${formatQtyDisplay(productionQty)} ${unit}`
                                  : `${formatQtyDisplay(item.qtyPerAdult)} ${unit} (fixed)`}
                              </small>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger flex-shrink-0"
                              onClick={() => removeSelectedItem(item.id)}
                              title="Remove item"
                            >
                              ×
                            </button>
                          </div>

                          <div className="row g-2 mt-2 align-items-end">
                            <div className="col-4">
                              <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Qty / adult</label>
                              <div className="input-group input-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => updateQtyField(item.id, 'qtyPerAdult', -0.1)}
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control text-center"
                                  value={item.qtyPerAdult ?? item.qtyPerPerson}
                                  onChange={(event) => {
                                    const value = Math.max(0, Number(event.target.value) || 0);
                                    updateSelectedItem(item.id, { qtyPerAdult: value, qtyPerPerson: value });
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => updateQtyField(item.id, 'qtyPerAdult', 0.1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <div className="col-4">
                              <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Qty / kid</label>
                              <div className="input-group input-group-sm">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => updateQtyField(item.id, 'qtyPerKid', -0.1)}
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  className="form-control text-center"
                                  value={item.qtyPerKid ?? 0}
                                  onChange={(event) => {
                                    const value = Math.max(0, Number(event.target.value) || 0);
                                    updateSelectedItem(item.id, { qtyPerKid: value });
                                  }}
                                />
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary"
                                  onClick={() => updateQtyField(item.id, 'qtyPerKid', 0.1)}
                                >
                                  +
                                </button>
                              </div>
                            </div>
                            <div className="col-4">
                              <label className="form-label mb-1" style={{ fontSize: '0.75rem' }}>Unit</label>
                              <select
                                className="form-select form-select-sm"
                                value={item.servingUnit || 'pcs'}
                                onChange={(event) => updateSelectedItem(item.id, { servingUnit: event.target.value })}
                              >
                                {SERVING_UNITS.map((unitOption) => (
                                  <option key={unitOption} value={unitOption}>{unitOption}</option>
                                ))}
                              </select>
                            </div>
                            <div className="col-4">
                              <div className="form-check mb-1">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`scale-${item.id}`}
                                  checked={item.scaleByPersons !== false}
                                  onChange={(event) => updateSelectedItem(item.id, { scaleByPersons: event.target.checked })}
                                />
                                <label className="form-check-label" htmlFor={`scale-${item.id}`} style={{ fontSize: '0.75rem' }}>
                                  Scale by headcount
                                </label>
                              </div>
                              <div className="fw-semibold text-end" style={{ fontSize: '0.85rem' }}>
                                {formatAmount(lineTotal)}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="border-top pt-3 mt-3 flex-shrink-0">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted">
                    {selectedItems.length} item(s) — {packageForm.persons} persons
                    ({headcounts.adults} adults, {headcounts.kids} kids)
                  </span>
                  <span className="fw-semibold">{formatAmount(selectedTotal)}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-primary w-100"
                  onClick={savePackage}
                  disabled={selectedItems.length === 0 || saving || loadingPackage}
                >
                  {saving
                    ? 'Saving...'
                    : editingPackageId
                      ? 'Update Package'
                      : 'Create Package'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm rounded-4">
        <div className="card-body p-4">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <h5 className="mb-0">Existing Packages</h5>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: 280 }}
              placeholder="Search packages..."
              value={packageSearchText}
              onChange={(event) => setPackageSearchText(event.target.value)}
            />
          </div>
          <TsrAdminDataTable
            columns={packageColumns}
            data={filteredPackages}
            loading={packagesLoading}
            noDataComponent="No packages found."
          />
        </div>
      </div>

      {descriptionModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title mb-0">{descriptionModal.name}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setDescriptionModal(null)}
                  aria-label="Close"
                />
              </div>
              <div className="modal-body">
                <p className="mb-0 text-muted" style={{ whiteSpace: 'pre-wrap' }}>
                  {descriptionModal.description}
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setDescriptionModal(null)}
                >
                  Show less
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TsrAdminCreatePackagePage;
